import { useCallback, useState } from 'react';
import * as core from './themeHistoryCore';
import type { HistoryState } from './themeHistoryCore';

export interface ThemeHistory {
  value: string;
  setUserValue: (v: string | ((prev: string) => string)) => void;
  applyPill: (v: string) => void;
  applySoften: (v: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  position: number;
  total: number;
  isDirty: boolean;
  reset: (initialValue?: string) => void;
}

/**
 * React binding of hooks/themeHistoryCore.ts — the idea box's back/forth
 * history. All rules (insert-after-cursor, blank initial state, dirty flag)
 * live in the core so they can be unit-tested and mirrored elsewhere.
 */
export default function useThemeHistory(initial: string = ''): ThemeHistory {
  const [state, setState] = useState<HistoryState>(() => core.initialState(initial));

  const setUserValue = useCallback((v: string | ((prev: string) => string)) => {
    setState(prev => core.setUserValue(prev, typeof v === 'function' ? v(prev.entries[prev.cursor].value) : v));
  }, []);
  const applyPill = useCallback((v: string) => setState(prev => core.applyPill(prev, v)), []);
  const applySoften = useCallback((v: string) => setState(prev => core.applySoften(prev, v)), []);
  const undo = useCallback(() => setState(core.undo), []);
  const redo = useCallback(() => setState(core.redo), []);
  const reset = useCallback((initialValue: string = '') => setState(core.initialState(initialValue)), []);

  const v = core.view(state);
  return {
    value: v.value,
    setUserValue,
    applyPill,
    applySoften,
    undo,
    redo,
    canUndo: v.canUndo,
    canRedo: v.canRedo,
    position: v.position,
    total: v.total,
    isDirty: v.isDirty,
    reset,
  };
}
